import React, { useState, useEffect, useRef } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import RichEditor from '../../components/common/RichEditor';
import { getAdminPopups, createPopup, updatePopup, deletePopup } from '../../api/content';
import { fetchWithAuth } from '../../api/apiClient';
import { BASE_URL } from '../../api/inquiries';
import AdminButton from '../../components/admin/AdminButton';

const PopupManagement = () => {
    const [popups, setPopups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);
    const editorRef = useRef(null);

    const [formData, setFormData] = useState({
        title: '',
        imageUrl: '',
        linkUrl: '',
        startDate: '',
        endDate: '',
        isVisible: true,
        targetPage: 'all',
        useEditor: false, // Toggle between image and rich text
        allowDismiss: true, // Allow "don't show today"
    });

    useEffect(() => {
        loadPopups();
    }, []);

    const loadPopups = async () => {
        try {
            const data = await getAdminPopups();
            setPopups(data);
        } catch (error) {
            console.error('Failed to load popups', error);
        } finally {
            setLoading(false);
        }
    };

    const openModal = (popup = null) => {
        if (popup) {
            setIsEditMode(true);
            setEditingId(popup.id);
            setFormData({
                title: popup.title || '',
                imageUrl: popup.imageUrl || '',
                linkUrl: popup.linkUrl || '',
                startDate: popup.startDate ? popup.startDate.slice(0, 16) : '',
                endDate: popup.endDate ? popup.endDate.slice(0, 16) : '',
                isVisible: popup.isVisible ?? true,
                targetPage: popup.targetPage || 'all',
                useEditor: !!popup.content,
                allowDismiss: popup.allowDismiss ?? true,
            });
            setTimeout(() => {
                if (editorRef.current && popup.content) {
                    editorRef.current.setHTML(popup.content);
                }
            }, 200);
        } else {
            setIsEditMode(false);
            setEditingId(null);
            setFormData({
                title: '',
                imageUrl: '',
                linkUrl: '',
                startDate: '',
                endDate: '',
                isVisible: true,
                targetPage: 'all',
                useEditor: false,
                allowDismiss: true,
            });
        }
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const uploadData = new FormData();
        uploadData.append('file', file);

        try {
            const res = await fetchWithAuth(`${BASE_URL}/api/upload?folder=tmp`, {
                method: 'POST',
                body: uploadData
            });
            if (!res.ok) throw new Error('Upload failed');
            const data = await res.json();
            setFormData(prev => ({ ...prev, imageUrl: data.url || data.path }));
        } catch (error) {
            console.error('Upload failed', error);
            alert('이미지 업로드 실패');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            const content = formData.useEditor ? (editorRef.current?.getHTML() || '') : null;
            const payload = {
                title: formData.title,
                content,
                imageUrl: formData.imageUrl || null,
                linkUrl: formData.linkUrl || null,
                startDate: formData.startDate || null,
                endDate: formData.endDate || null,
                isVisible: formData.isVisible,
                targetPage: formData.targetPage,
                allowDismiss: formData.allowDismiss,
            };

            if (isEditMode) {
                await updatePopup(editingId, payload);
                alert('수정되었습니다.');
            } else {
                await createPopup(payload);
                alert('등록되었습니다.');
            }
            closeModal();
            loadPopups();
        } catch (error) {
            console.error('Failed to save', error);
            alert('저장에 실패했습니다.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('정말 삭제하시겠습니까?')) {
            try {
                await deletePopup(id);
                alert('삭제되었습니다.');
                loadPopups();
            } catch (error) {
                console.error('Failed to delete', error);
                alert('삭제 실패');
            }
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('ko-KR', {
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit'
        });
    };

    const getStatus = (popup) => {
        const now = new Date();
        const start = popup.startDate ? new Date(popup.startDate) : null;
        const end = popup.endDate ? new Date(popup.endDate) : null;

        if (!popup.isVisible) return { label: '비공개', bg: 'bg-gray-500', text: 'text-white' };
        if (end && now > end) return { label: '종료', bg: 'bg-red-500', text: 'text-white' };
        if (start && now < start) return { label: '예정', bg: 'bg-yellow-500', text: 'text-white' };
        return { label: '진행중', bg: 'bg-green-500', text: 'text-white' };
    };

    return (
        <AdminLayout>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
                    <h1 className="text-2xl font-bold tracking-tight text-gray-900">팝업 관리</h1>
                    <AdminButton variant="primary" onClick={() => openModal(null)}>
                        + 팝업 등록
                    </AdminButton>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    {loading ? (
                        <div className="flex justify-center items-center h-64">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">이미지</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">제목</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">대상 페이지</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">시작일</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">종료일</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">상태</th>
                                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-32">관리</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {popups.map(popup => {
                                        const status = getStatus(popup);
                                        return (
                                            <tr key={popup.id} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {popup.imageUrl ? (
                                                        <img
                                                            src={popup.imageUrl}
                                                            alt={popup.title}
                                                            className="w-12 h-12 object-cover rounded-md border border-gray-200"
                                                        />
                                                    ) : (
                                                        <div className="w-12 h-12 bg-gray-100 rounded-md border border-gray-200 flex items-center justify-center text-gray-400 text-xs text-center p-1">
                                                            {popup.content ? 'HTML\n에디터' : '이미지\n없음'}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="text-sm font-medium text-gray-900">{popup.title}</div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {popup.targetPage === 'all' ? '전체' : popup.targetPage}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {formatDate(popup.startDate)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {formatDate(popup.endDate)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
                                                        {status.label}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <div className="flex justify-end gap-2">
                                                        <AdminButton variant="secondary" size="sm" onClick={() => openModal(popup)}>수정</AdminButton>
                                                        <AdminButton variant="danger" size="sm" onClick={() => handleDelete(popup.id)}>삭제</AdminButton>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {popups.length === 0 && (
                                        <tr>
                                            <td colSpan="7" className="px-6 py-12 text-center text-sm text-gray-500">
                                                등록된 팝업이 없습니다.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Popup Modal */}
                {isModalOpen && (
                    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={closeModal}></div>
                            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                            <div className="relative inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle w-full max-w-4xl">
                                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 max-h-[85vh] overflow-y-auto">
                                    <div className="sm:flex sm:items-start">
                                        <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                                            <h3 className="text-xl leading-6 font-bold text-gray-900 mb-6" id="modal-title">
                                                {isEditMode ? '팝업 수정' : '팝업 등록'}
                                            </h3>
                                            <form onSubmit={handleSubmit} className="space-y-6">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">제목 *</label>
                                                    <input
                                                        type="text"
                                                        name="title"
                                                        value={formData.title}
                                                        onChange={handleChange}
                                                        required
                                                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-3 border bg-white"
                                                        placeholder="팝업 제목"
                                                    />
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">시작일시</label>
                                                        <input
                                                            type="datetime-local"
                                                            name="startDate"
                                                            value={formData.startDate}
                                                            onChange={handleChange}
                                                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-3 border bg-white"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">종료일시</label>
                                                        <input
                                                            type="datetime-local"
                                                            name="endDate"
                                                            value={formData.endDate}
                                                            onChange={handleChange}
                                                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-3 border bg-white"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">대상 페이지</label>
                                                        <select
                                                            name="targetPage"
                                                            value={formData.targetPage}
                                                            onChange={handleChange}
                                                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-3 border bg-white"
                                                        >
                                                            <option value="all">전체</option>
                                                            <option value="home">홈</option>
                                                            <option value="solutions">솔루션</option>
                                                            <option value="products">제품소개</option>
                                                            <option value="use-cases">적용사례</option>
                                                            <option value="demo">데모</option>
                                                            <option value="estimate">예상견적</option>
                                                            <option value="contact">문의하기</option>
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">공개 여부</label>
                                                        <div className="flex items-center pt-1">
                                                            <label className="flex items-center gap-2 cursor-pointer touch-manipulation">
                                                                <input
                                                                    type="checkbox"
                                                                    name="isVisible"
                                                                    checked={formData.isVisible}
                                                                    onChange={handleChange}
                                                                    className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                                />
                                                                <span className="text-sm text-gray-700">공개</span>
                                                            </label>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">"오늘 보지 않기" 허용</label>
                                                        <div className="flex items-center pt-1">
                                                            <label className="flex items-center gap-2 cursor-pointer touch-manipulation">
                                                                <input
                                                                    type="checkbox"
                                                                    name="allowDismiss"
                                                                    checked={formData.allowDismiss}
                                                                    onChange={handleChange}
                                                                    className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                                />
                                                                <span className="text-sm text-gray-700">허용</span>
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">링크 URL (클릭 시 이동)</label>
                                                    <input
                                                        type="text"
                                                        name="linkUrl"
                                                        value={formData.linkUrl}
                                                        onChange={handleChange}
                                                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-3 border bg-white"
                                                        placeholder="https://..."
                                                    />
                                                </div>

                                                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                                    <label className="flex items-center gap-2 cursor-pointer touch-manipulation mb-4">
                                                        <input
                                                            type="checkbox"
                                                            name="useEditor"
                                                            checked={formData.useEditor}
                                                            onChange={handleChange}
                                                            className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                        />
                                                        <span className="text-sm font-medium text-gray-900">고급 에디터 사용 (이미지 대신 HTML 콘텐츠)</span>
                                                    </label>

                                                    {formData.useEditor ? (
                                                        <div className="mt-2">
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">콘텐츠</label>
                                                            <div className="bg-white">
                                                                <RichEditor ref={editorRef} height="350px" placeholder="팝업 내용을 작성하세요..." />
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="mt-2">
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">팝업 이미지</label>
                                                            <input type="file" onChange={handleImageUpload} className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer" accept="image/*" />
                                                            {formData.imageUrl && (
                                                                <div className="mt-4 bg-white p-2 rounded-lg border border-gray-200 inline-block">
                                                                    <img src={formData.imageUrl} alt="Preview" className="max-h-48 object-contain rounded" />
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </form>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-4 py-4 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200 rounded-b-xl gap-2">
                                    <AdminButton type="submit" variant="primary" onClick={handleSubmit} disabled={saving}>
                                        {saving ? '저장 중...' : (isEditMode ? '수정' : '등록')}
                                    </AdminButton>
                                    <AdminButton type="button" variant="secondary" onClick={closeModal}>
                                        취소
                                    </AdminButton>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
};

export default PopupManagement;
